/**
 * Whether a horizontal scroller has content hidden past its left or right
 * edge, for the fade that tells a cut-off column from a short value
 * (docs/ux-review.md P1-1).
 */
import { useEffect, useState, type RefObject } from "react";

export interface ScrollEdges {
  /** content hidden to the left (scrolled) */
  left: boolean;
  /** content hidden to the right */
  right: boolean;
  /** width of a classic vertical scrollbar, so a fade sits beside it */
  gutter: number;
}

type Box = Pick<HTMLElement, "scrollLeft" | "clientWidth" | "scrollWidth" | "offsetWidth">;

export function scrollEdges(el: Box): ScrollEdges {
  return {
    left: el.scrollLeft > 1,
    right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    gutter: Math.max(0, el.offsetWidth - el.clientWidth),
  };
}

const same = (a: ScrollEdges, b: ScrollEdges) =>
  a.left === b.left && a.right === b.right && a.gutter === b.gutter;

/** Tracks the scroller in `ref` (mounted on the first render) through scrolls and resizes of it
 *  and of its children. */
export function useScrollEdges(ref: RefObject<HTMLElement | null>): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ left: false, right: false, gutter: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () =>
      setEdges((prev) => {
        const next = scrollEdges(el);
        return same(prev, next) ? prev : next;
      });
    const ro = new ResizeObserver(update);
    const observeAll = () => {
      ro.observe(el);
      for (const c of Array.from(el.children)) ro.observe(c);
    };
    // children swapped (empty state ↔ rows) must be measured too
    const mo = new MutationObserver(() => {
      observeAll();
      update();
    });
    observeAll();
    mo.observe(el, { childList: true });
    el.addEventListener("scroll", update, { passive: true });
    update();
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
      mo.disconnect();
    };
  }, [ref]);
  return edges;
}
